import { GrowthBook } from '@growthbook/growthbook-react';

import config from 'config';
import { recordExperimentAssignment } from 'services/experimentAssignment';
import { getBrowserTimezone, resolveCountry } from 'services/i18n';
import type { Country } from 'services/i18n';
import { rumService } from 'services/rum';
import { isMonitoringAgentOrBot } from 'utils/browser';
import {
  GROWTHBOOK_DECISION_FLAGS,
  GROWTHBOOK_ENUM_FLAGS,
  experimentVariantManager,
} from 'utils/experimentation';

const isProd = config.PARTNERS_ENVIRONMENT === 'production';
const TRACKED_GROWTHBOOK_FLAGS = new Set([
  ...GROWTHBOOK_DECISION_FLAGS,
  ...Object.keys(GROWTHBOOK_ENUM_FLAGS),
]);

/**
 * Sanitizes a GrowthBook feature flag key to be compatible with Datadog RUM.
 *
 * GrowthBook uses dot-delimited keys (e.g., `partners.acquisition.flyer-requirement`)
 * but Datadog interprets dots as object path separators, creating nested structures
 * instead of a single flag name. Dashes are also unsupported characters.
 *
 * This function replaces both `.` and `-` with `_` to ensure proper flag tracking.
 *
 * @example
 * sanitizeFlagKeyForDatadog('partners.acquisition.flyer-requirement')
 * // Returns: 'partners_acquisition_flyer_requirement'
 *
 * @see https://docs.datadoghq.com/real_user_monitoring/feature_flag_tracking/setup/
 */
function sanitizeFlagKeyForDatadog(key: string): string {
  return key.replaceAll('.', '_').replaceAll('-', '_');
}

type DefaultIds = {
  sessionId: string;
  anonymousId: string;
};

function buildDefaultAttributes({
  sessionId,
  anonymousId,
  isBot,
}: DefaultIds & {
  isBot: boolean;
}) {
  const country = resolveCountry();

  return {
    app_version: config.REACT_APP_VERSION,
    country,
    // Seed from the browser-inferred country so country_code-targeted flags
    // (e.g. partners.acquisition.wellhub-usc-flow) can evaluate on the very
    // first render, before a lead exists. Overridden by the selected country
    // via useCountryChange / setLeadExperimentAttributes once one is chosen.
    country_code: country.toUpperCase() as Uppercase<Country>,
    platform: 'web',
    timezone: getBrowserTimezone(),
    user_agent: navigator.userAgent,
    session_id: sessionId,
    anonymous_id: isBot ? undefined : anonymousId,
    is_bot: isBot,
    // application identifier for USER_EXPERIMENT_API
    app_id: config.APP_NAME,
  } as const;
}

/**
 * Initializes a GrowthBook client instance with default user attributes.
 *
 * The instance is configured with:
 * - API connection details from environment variables
 * - Initial user attributes (app_version, country, platform, timezone)
 * - A tracking callback that records experiment assignments to the backend
 * - Dev mode enabled for non-production environments
 *
 * @returns An initialized GrowthBook client instance ready for feature flag evaluation
 */

type GrowthBookEnumFlagValues = {
  [Key in keyof typeof GROWTHBOOK_ENUM_FLAGS]: (typeof GROWTHBOOK_ENUM_FLAGS)[Key][number];
};

// TODO: these typings can be auto-generated: https://docs.growthbook.io/tools/cli
export type GrowthBookFlags = Record<
  (typeof GROWTHBOOK_DECISION_FLAGS)[number],
  boolean
> &
  GrowthBookEnumFlagValues;

export function initGrowthBook({ anonymousId, sessionId }: DefaultIds) {
  const isBot = isMonitoringAgentOrBot();

  const gb = new GrowthBook<GrowthBookFlags>({
    onFeatureUsage: (featureKey, result) => {
      // A null evaluation means the flag couldn't be resolved (not in the
      // payload yet, or the fetch failed): there is no decision to record,
      // and recording one would write a literal "<flag>=null" token into the
      // lead's experimentVariant.
      if (result.value == null) {
        return;
      }

      if (TRACKED_GROWTHBOOK_FLAGS.has(featureKey)) {
        experimentVariantManager.upsertDecision({
          key: featureKey,
          value: result.value,
          source: 'growthbook',
        });
      }

      rumService.addFeatureFlagEvaluation(
        sanitizeFlagKeyForDatadog(featureKey),
        result.value,
      );
      if (!isProd) {
        // eslint-disable-next-line no-console
        console.log('[GrowthBook] feature', featureKey, 'result:', result);
      }
    },
    apiHost: config.GROWTHBOOK_API_HOST,
    clientKey: config.GROWTHBOOK_CLIENT_KEY,
    enableDevMode: !isProd,
    remoteEval: true,
    attributes: buildDefaultAttributes({ sessionId, anonymousId, isBot }),
    trackingCallback: (experiment, result) => {
      recordExperimentAssignment({
        result: { experiment, experimentResult: result },
        clientId: config.APP_NAME,
        participantType: result.hashAttribute,
        evaluatedAt: new Date().toISOString(),
        context: gb.getAttributes(),
      });

      if (!isProd) {
        // eslint-disable-next-line no-console
        console.log('[GrowthBook] Decision event', {
          experimentId: experiment.key,
          variationId: result.key,
        });
      }
    },
  });

  // promise updates are handled in Provider
  // https://docs.growthbook.io/lib/react#quick-usage
  gb.init().then(result => {
    if (isBot) {
      return result;
    }

    // The SDK throws `HTTP error: ${status}` on a non-ok fetch (see
    // @growthbook/growthbook's feature-repository.js); a 403 here is assumed
    // to be the same WAF geo-blocking behavior confirmed on the sibling
    // user-experiment-api, not an app-side failure — don't report it as a RUM error.
    const isWafBlocked =
      result?.error instanceof Error &&
      result.error.message === 'HTTP error: 403';

    if (!result.success && result.source !== 'timeout' && !isWafBlocked) {
      rumService.addError(
        new Error(`[GrowthBook][init] error: ${result?.error}`),
      );
    }
    return result;
  });

  return gb;
}

export type GrowthBookApp = ReturnType<typeof initGrowthBook>;
